// Shopify Analytics SDK — IIFE entry.
//
// Self-registers with PixelRuntime and forwards events to Shopify monorail
// via @shopify/hydrogen-react's analytics functions.
//
// Loaded as <script src="…/shopify-pixel-sdk.js"> in the storefront layout.
// Config key: 'shopify-pixel'  (storefront pixelConfig entry can be `{}`).
//
// Per-shop config is fetched at runtime from the backend's /sdk-config
// endpoint — no per-merchant env vars or build-time configuration needed
// on the storefront side.

import { bootstrapConfig } from './bootstrap';
import { readShopifyCookies, resolveCookieDomain } from './cookies';
import {
	resolveConfig,
	sendAddToCart,
	sendCheckout,
	sendPageView,
	sendProductView,
	type ResolvedConfig,
} from './events';
import { resolveTrackingTokens, type TrackingTokens } from './tracking';
import type { PixelRegistration, SDKEvent } from './types';

const TAG = '[ShopifyPixelSDK]';
const STYLE = 'color: #95BF47; font-weight: bold;'; // Shopify green

function log(category: string, ...args: unknown[]) {
	// eslint-disable-next-line no-console
	console.log(`%c${TAG} [${category}]`, STYLE, ...args);
}

// Buffer events that arrive before the async config bootstrap completes.
// Capped at 50 so a long config-fetch can't OOM the page.
const PRE_BOOTSTRAP_QUEUE_MAX = 50;
type QueuedEvent = { kind: 'page' | 'view' | 'cart' | 'checkout'; event: SDKEvent };

const registration: PixelRegistration = {
	name: 'shopify-pixel',
	register(analytics) {
		log('Init', 'register() called — bootstrapping config from backend');

		// Skip Lighthouse runs (matches hydrogen-react behaviour).
		if (typeof navigator !== 'undefined' && /Chrome-Lighthouse/.test(navigator.userAgent)) {
			log('Init', 'Lighthouse detected — dispatch disabled for this run');
			return;
		}

		let resolvedCfg: ResolvedConfig | null = null;
		let tokens: TrackingTokens | null = null;
		const pending: QueuedEvent[] = [];

		function dispatch(q: QueuedEvent) {
			if (!resolvedCfg || !tokens) return;
			switch (q.kind) {
				case 'page':
					sendPageView(resolvedCfg, tokens, q.event);
					break;
				case 'view':
					sendProductView(resolvedCfg, tokens, q.event);
					break;
				case 'cart':
					void sendAddToCart(resolvedCfg, tokens, q.event);
					break;
				case 'checkout':
					sendCheckout(resolvedCfg, tokens, q.event);
					break;
			}
		}

		function enqueue(kind: QueuedEvent['kind'], event: SDKEvent) {
			if (resolvedCfg && tokens) {
				dispatch({ kind, event });
				return;
			}
			if (pending.length >= PRE_BOOTSTRAP_QUEUE_MAX) {
				pending.shift(); // drop oldest
			}
			pending.push({ kind, event });
		}

		// Subscribe IMMEDIATELY so we don't miss the replay PixelRuntime fires
		// during the registration window. Anything that arrives before config
		// resolves goes into `pending` and ships when bootstrap completes.
		analytics.subscribe('PageView',         (event) => enqueue('page',     event));
		analytics.subscribe('ViewContent',      (event) => enqueue('view',     event));
		analytics.subscribe('AddToCart',        (event) => enqueue('cart',     event));
		analytics.subscribe('InitiateCheckout', (event) => enqueue('checkout', event));

		// Debug surface — exposed regardless of bootstrap state so ops can
		// poke at it from the console.
		try {
			window.__shopifyPixelDebug = {
				isSessionValid: () => {
					const c = readShopifyCookies();
					return Boolean(c.unique && c.visit);
				},
				getCookies: readShopifyCookies,
			};
		} catch {
			/* noop */
		}

		// Bootstrap async, then drain the queue.
		void bootstrapConfig().then(async (rawCfg) => {
			if (!rawCfg) {
				log('Init', 'bootstrap returned null — disabling SDK; events dropped');
				pending.length = 0;
				return;
			}
			let cfg: ResolvedConfig;
			try {
				cfg = resolveConfig(rawCfg);
			} catch (err) {
				log('Init', 'config validation failed; disabling SDK:', err);
				pending.length = 0;
				return;
			}

			const cookieDomain = resolveCookieDomain(cfg.domain);
			const resolvedTokens = await resolveTrackingTokens(cookieDomain, cfg.hasUserConsent);
			if (!resolvedTokens) {
				log('Init', 'no Shopify-issued tracking tokens — disabling SDK; events dropped');
				pending.length = 0;
				return;
			}

			resolvedCfg = cfg;
			tokens = resolvedTokens;
			log('Init', 'tokens ready', {
				unique: tokens.uniqueToken.slice(0, 8) + '…',
				visit: tokens.visitToken.slice(0, 8) + '…',
				domain: cookieDomain || '(host-only)',
			});
			log('Init', `ready — flushing ${pending.length} queued event(s)`);

			while (pending.length) {
				const q = pending.shift();
				if (q) dispatch(q);
			}
		});
	},
};

log('Init', 'Initializing shopify-pixel-sdk');

if (typeof window !== 'undefined') {
	if (window.__OPEN_STORE_PIXEL_RUNTIME__) {
		log('Init', 'Runtime found — registering immediately');
		window.__OPEN_STORE_PIXEL_RUNTIME__.register(registration);
	} else {
		log('Init', 'Runtime not ready — pushing to pending queue');
		window.__OPEN_STORE_PIXEL_PENDING__ = window.__OPEN_STORE_PIXEL_PENDING__ || [];
		window.__OPEN_STORE_PIXEL_PENDING__.push(registration);
	}
}
